import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  GraduationCap, 
  ArrowRight, 
  Loader2,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Trophy,
  Eye,
  EyeOff,
  Calendar,
  FileText
} from 'lucide-react';
import { renderSafeHtml } from '../components/RichTextEditor';

const isChoice = (q) => q.question_type === 'multiple_choice' && Array.isArray(q.options) && q.options.length > 0;

const StudentExamResults = () => {
  const { examId, sessionId } = useParams();
  const navigate = useNavigate();
  const { token, isStudent, isApprovedStudent } = useAuth();
  
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Auth is enforced by ApprovedStudentRoute; just wait until we have a token.
    if (!token) return;
    fetchResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, sessionId]);

  const fetchResults = async () => {
    try {
      const response = await fetch(`${API_URL}/api/student/exam-sessions/${sessionId}/results`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setResults(data);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'فشل تحميل النتائج');
      }
    } catch (error) {
      console.error('Error fetching results:', error);
      setError('حدث خطأ أثناء تحميل النتائج');
    }
    setLoading(false);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">مكتمل</Badge>;
      case 'terminated':
        return <Badge variant="destructive">منهي بمخالفة</Badge>;
      case 'blocked':
        return <Badge className="bg-red-100 text-red-800">تم الإيقاف</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getGradeBadge = (percentage) => {
    if (percentage >= 90) return <Badge className="bg-[#EF9F27]/15 text-[#854F0B] text-lg px-4 py-1">ممتاز</Badge>;
    if (percentage >= 80) return <Badge className="bg-blue-100 text-blue-800 text-lg px-4 py-1">جيد جداً</Badge>;
    if (percentage >= 70) return <Badge className="bg-cyan-100 text-cyan-800 text-lg px-4 py-1">جيد</Badge>;
    if (percentage >= 60) return <Badge className="bg-yellow-100 text-yellow-800 text-lg px-4 py-1">مقبول</Badge>;
    return <Badge className="bg-red-100 text-red-800 text-lg px-4 py-1">راسب</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#EF9F27] mx-auto mb-4" />
          <p className="text-[#6B4C2A]">جاري تحميل النتائج...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-[#FDF8F0] border border-[#E8DFCF]">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-[#412402] mb-2">خطأ</h2>
            <p className="text-[#6B4C2A] mb-6">{error}</p>
            <Button 
              onClick={() => navigate('/student/exams')}
              className="bg-[#EF9F27] hover:bg-[#D88E1F] text-[#854F0B] font-bold"
            >
              العودة للاختبارات
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Results not available yet
  if (!results.can_view) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-[#FDF8F0] border border-[#E8DFCF]">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-[#EF9F27]/15 rounded-full flex items-center justify-center mx-auto mb-4">
              <EyeOff className="w-8 h-8 text-[#EF9F27]" />
            </div>
            <h2 className="text-xl font-bold text-[#412402] mb-2">النتائج غير متاحة بعد</h2>
            <p className="text-[#6B4C2A] mb-4">{results.message}</p>
            
            {results.release_mode === 'scheduled' && results.release_datetime && (
              <div className="bg-[#EF9F27]/10 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center gap-2 text-[#854F0B]">
                  <Calendar className="w-5 h-5" />
                  <span>ستتوفر النتائج في:</span>
                </div>
                <p className="text-lg font-bold text-[#412402] mt-2">
                  {new Date(results.release_datetime).toLocaleString('ar-SA')}
                </p>
              </div>
            )}
            
            {results.release_mode === 'manual' && (
              <p className="text-sm text-[#8B7355] mb-6">
                سيتم إصدار النتائج من قبل المعلم قريباً
              </p>
            )}
            
            <Button 
              onClick={() => navigate('/student/exams')}
              className="bg-[#EF9F27] hover:bg-[#D88E1F] text-[#854F0B] font-bold"
            >
              العودة للاختبارات
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      {/* Header */}
      <header className="bg-[#FDF8F0] border-b border-[#E8DFCF] sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.png" alt="Logo" className="w-10 h-10 rounded-lg object-cover border border-[#E8DFCF]" />
              <span className="text-xl font-bold text-[#412402]">المعتمد في اللغة العربية</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/student/exams')}
            className="text-[#6B4C2A] hover:text-[#412402] mb-4"
          >
            <ArrowRight className="w-4 h-4 ml-2" />
            العودة للاختبارات
          </Button>
          <h1 className="text-2xl font-bold text-[#412402]">نتيجة الاختبار</h1>
          <p className="text-[#6B4C2A] mt-1">{results.exam_title}</p>
        </div>

        {/* Custom Message from Teacher */}
        {results.custom_message && (
          <Card className="bg-[#EF9F27]/10 border border-[#EF9F27]/30 mb-6">
            <CardContent className="p-4">
              <p className="text-[#854F0B] text-center">{results.custom_message}</p>
            </CardContent>
          </Card>
        )}

        {/* Pass/Fail Only Card */}
        {results.pass_fail_message && !results.score && (
          <Card className="bg-[#FDF8F0] border border-[#E8DFCF] mb-6">
            <CardContent className="p-8 text-center">
              <div className={`w-24 h-24 ${results.passed ? 'bg-green-100' : 'bg-red-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                {results.passed ? (
                  <CheckCircle className="w-12 h-12 text-green-600" />
                ) : (
                  <XCircle className="w-12 h-12 text-red-600" />
                )}
              </div>
              <p className={`text-3xl font-bold ${results.passed ? 'text-green-600' : 'text-red-600'}`}>
                {results.pass_fail_message}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Score Card - Only if score is visible */}
        {results.score !== undefined && (
          <Card className="bg-[#FDF8F0] border border-[#E8DFCF] mb-6">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-[#EF9F27]/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trophy className="w-10 h-10 text-[#EF9F27]" />
              </div>
              <div className="mb-4">
                <span className="text-5xl font-bold text-[#412402]">{results.score}</span>
                <span className="text-2xl text-[#6B4C2A]">/{results.total_points}</span>
              </div>
              {results.percentage !== undefined && (
                <>
                  {getGradeBadge(results.percentage)}
                  <p className="text-lg text-[#6B4C2A] mt-3">{results.percentage}%</p>
                  <Progress value={results.percentage} className="mt-4 h-3" />
                </>
              )}
              {/* Rank Display */}
              {results.rank && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-blue-800 font-medium">
                    ترتيبك: <span className="text-2xl font-bold">{results.rank}</span> من أصل {results.total_students} طالب
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Exam Info */}
        <Card className="bg-[#FDF8F0] border border-[#E8DFCF] mb-6">
          <CardContent className="p-6">
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-[#8B7355] mb-1">الحالة</p>
                {getStatusBadge(results.status)}
              </div>
              {results.started_at && (
                <div>
                  <p className="text-sm text-[#8B7355] mb-1">بداية الاختبار</p>
                  <p className="font-medium text-[#412402]">
                    {new Date(results.started_at).toLocaleString('ar-SA')}
                  </p>
                </div>
              )}
              {results.ended_at && (
                <div>
                  <p className="text-sm text-[#8B7355] mb-1">نهاية الاختبار</p>
                  <p className="font-medium text-[#412402]">
                    {new Date(results.ended_at).toLocaleString('ar-SA')}
                  </p>
                </div>
              )}
              {results.violation_count !== undefined && (
                <div>
                  <p className="text-sm text-[#8B7355] mb-1">المخالفات</p>
                  <p className="font-medium text-[#412402]">{results.violation_count}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Questions Review */}
        {results.questions_review && results.questions_review.length > 0 && (
          <Card className="bg-[#FDF8F0] border border-[#E8DFCF]">
            <CardHeader className="border-b border-[#E8DFCF]">
              <CardTitle className="text-lg font-semibold text-[#412402] flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#EF9F27]" />
                مراجعة الأسئلة
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 divide-y divide-[#E8DFCF]">
              {results.questions_review.map((question, index) => (
                <div key={question.question_id} className="p-4 hover:bg-[#FFFFFF]">
                  <div className="flex items-start gap-3 mb-3">
                    <span className="w-8 h-8 bg-[#EF9F27]/10 text-[#854F0B] rounded-lg flex items-center justify-center font-semibold shrink-0">
                      {index + 1}
                    </span>
                    <div className="flex-1">
                      <p
                        dir="auto"
                        className="font-medium text-[#412402] mb-2 leading-relaxed whitespace-pre-wrap break-words"
                        dangerouslySetInnerHTML={renderSafeHtml(question.question_text)}
                      />
                      <div className="flex items-center gap-2 text-sm">
                        <Badge variant="outline" className="border-[#E8DFCF] text-[#6B4C2A]">{question.points} درجة</Badge>
                        {question.is_correct !== undefined && (
                          question.is_correct ? (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3 ml-1" />
                              صحيح
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800">
                              <XCircle className="w-3 h-3 ml-1" />
                              خطأ
                            </Badge>
                          )
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Multiple choice: every option, with the student's pick and
                      (when the teacher allows it) the correct one marked. */}
                  {isChoice(question) && (
                    <div className="space-y-1 text-sm">
                      {question.options.map(opt => {
                        const isStudent = question.student_answer === opt.id;
                        const isCorrect = question.correct_answer === opt.id;
                        return (
                          <div key={opt.id} className={`flex items-start gap-2 p-2 rounded ${
                            isCorrect ? 'bg-green-100 text-green-800' :
                            isStudent && question.correct_answer !== undefined ? 'bg-red-100 text-red-800' :
                            isStudent ? 'bg-[#EF9F27]/15 text-[#412402]' :
                            'text-[#412402]/70'
                          }`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-current shrink-0 mt-2" />
                            <span dir="auto" className="flex-1 min-w-0 break-words" dangerouslySetInnerHTML={renderSafeHtml(opt.text)} />
                            {isStudent && <Badge variant="outline" className="text-xs shrink-0">إجابتك</Badge>}
                            {isCorrect && <Badge className="text-xs bg-green-200 text-green-800 shrink-0">الصحيحة</Badge>}
                          </div>
                        );
                      })}
                      {question.student_answer === '' && (
                        <p className="text-xs text-amber-700 pt-1">لم تجب عن هذا السؤال</p>
                      )}
                    </div>
                  )}

                  {/* Student Answer */}
                  {!isChoice(question) && question.student_answer !== undefined && (
                    <div className={`rounded-lg p-3 mb-2 ${
                      question.is_correct === true ? 'bg-green-50 border border-green-200' :
                      question.is_correct === false ? 'bg-red-50 border border-red-200' :
                      'bg-[#FFFFFF] border border-[#E8DFCF]'
                    }`}>
                      <p className="text-sm text-[#8B7355] mb-1">إجابتك:</p>
                      <p dir="auto" className="font-medium text-[#412402] whitespace-pre-wrap break-words">
                        {question.question_type === 'true_false' ? (
                          question.student_answer === 'true' ? 'صح' : 
                          question.student_answer === 'false' ? 'خطأ' : 'لم تجب'
                        ) : (
                          question.student_answer || 'لم تجب'
                        )}
                      </p>
                    </div>
                  )}

                  {/* Correct Answer */}
                  {!isChoice(question) && question.correct_answer !== undefined && !question.is_correct && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm text-green-700 mb-1">الإجابة الصحيحة:</p>
                      <p dir="auto" className="font-medium text-green-900 whitespace-pre-wrap break-words">
                        {question.question_type === 'true_false' ? (
                          question.correct_answer === 'true' ? 'صح' : 'خطأ'
                        ) : (
                          question.correct_answer
                        )}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Back Button */}
        <div className="mt-8 text-center">
          <Button 
            onClick={() => navigate('/student/exams')}
            className="bg-[#EF9F27] hover:bg-[#D88E1F] text-[#854F0B] font-bold"
          >
            العودة لقائمة الاختبارات
          </Button>
        </div>
      </main>
    </div>
  );
};

export default StudentExamResults;
